import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import './CheckInbox.css';

const CheckInbox = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isResending, setIsResending] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  
  const email = searchParams.get('email');
  const leadId = searchParams.get('lead_id');
  const plan = searchParams.get('plan');
  
  // Get correct API base URL (same logic as api/client.js)
  const getApiBaseUrl = () => {
    if (process.env.NODE_ENV === 'development') {
      return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    } else {
      // Use proxy approach for better security - Vercel forwards /api/* to backend
      return '/api';
    }
  };

  // Redirect if missing required params
  useEffect(() => {
    if (!email || !leadId) {
      navigate('/pricing');
    }
  }, [email, leadId, navigate]);

  // Check lead status and redirect if already verified
  useEffect(() => {
    const checkLeadStatus = async () => {
      if (!leadId) return;
      
      try {
        const response = await fetch(`${getApiBaseUrl()}/onboarding/lead/${leadId}`);
        const leadInfo = await response.json();
        
        if (leadInfo.status === 'email_verified' || leadInfo.status === 'verified') {
          // Email already verified, redirect to onboarding
          navigate(`/onboarding?step=1&lead_id=${leadId}`);
        }
      } catch (error) {
        console.error('Error checking lead status:', error);
        // Continue showing page if we can't check status
      }
    };
    
    checkLeadStatus();
  }, [leadId, navigate]);

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendMessage('');
    
    try {
      const response = await fetch(`${getApiBaseUrl()}/onboarding/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: leadId,
          email: email
        })
      });
      
      const result = await response.json();
      
      if (result.success) {
        if (result.action === 'already_verified') {
          // Email already verified, redirect to onboarding
          setResendMessage('✓ Email already verified! Redirecting to continue your signup...');
          setTimeout(() => {
            navigate(`/onboarding?step=1&lead_id=${leadId}`);
          }, 2000);
        } else {
          setResendMessage('✓ Verification email sent successfully! Please check your inbox.');
        }
      } else {
        setResendMessage(result.message || 'Failed to resend email. Please try again.');
      }
    } catch (error) {
      console.error('Resend email error:', error);
      setResendMessage('Network error. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const getEmailProvider = (email) => {
    const domain = email.split('@')[1]?.toLowerCase();
    if (domain?.includes('gmail')) return { name: 'Gmail', url: 'https://gmail.com' };
    if (domain?.includes('outlook') || domain?.includes('hotmail')) return { name: 'Outlook', url: 'https://outlook.com' };
    if (domain?.includes('yahoo')) return { name: 'Yahoo', url: 'https://yahoo.com' };
    if (domain?.includes('apple') || domain?.includes('icloud')) return { name: 'iCloud', url: 'https://icloud.com' };
    return null;
  };

  const emailProvider = getEmailProvider(email || '');

  return (
    <div className="check-inbox-page">
      <div className="container">
        <div className="inbox-content">
          {/* Header */}
          <div className="inbox-header">
            <div className="inbox-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                <polyline points="22,6 12,13 2,6"></polyline>
              </svg>
            </div>
            <h1>Check Your Inbox</h1>
            <p>We've sent a verification email to:</p>
            <div className="email-display">
              <strong>{email}</strong>
            </div>
          </div>

          {/* Instructions */}
          <div className="inbox-instructions">
            <div className="instruction-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Check your email</h3>
                <p>Look for an email from MedInsight Pro with the subject "Verify your email address"</p>
              </div>
            </div>
            
            <div className="instruction-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Click the verification link</h3>
                <p>Click the "Verify Email" button in the email to continue your {plan} plan setup</p>
              </div>
            </div>
            
            <div className="instruction-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Complete your setup</h3>
                <p>You'll be redirected to complete your clinic details and Pabau integration</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="inbox-actions">
            {emailProvider && (
              <a 
                href={emailProvider.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Open {emailProvider.name}
              </a>
            )}
            
            <button 
              onClick={handleResendEmail}
              disabled={isResending}
              className="btn btn-secondary"
            >
              {isResending ? 'Sending...' : 'Resend Email'}
            </button>
          </div>

          {/* Resend Message */}
          {resendMessage && (
            <div className={`resend-message ${resendMessage.includes('✓') ? 'success' : 'error'}`}>
              {resendMessage}
            </div>
          )}

          {/* Help Section */}
          <div className="inbox-help">
            <details>
              <summary>Didn't receive the email?</summary>
              <div className="help-content">
                <ul>
                  <li>Check your spam or junk folder</li>
                  <li>Make sure you entered the correct email address</li>
                  <li>Add noreply@medinsightpro.com to your contacts</li>
                  <li>Wait a few minutes - emails can sometimes be delayed</li>
                  <li>Try clicking "Resend Email" above</li>
                </ul>
                <p>Still having trouble? <a href="mailto:support@medinsightpro.com">Contact our support team</a></p>
              </div>
            </details>
          </div>

          {/* Back to Pricing */}
          <div className="back-link">
            <button 
              onClick={() => navigate('/pricing')}
              className="link-button"
            >
              ← Back to Pricing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckInbox; 